import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useUserStore } from '@/store/userStore';
import { getActiveTargets } from '@/utils/format';
import { buildWeekReport, type DayRow, type WeekReport } from '@/utils/weekInsights';

// Собирает данные недели для разбора: отметки состояния + еда по дням.
// Границы суток — МЕСТНЫЕ (та же ошибка с UTC уже стоила нам вечера,
// см. миграцию 011: еда после полуночи улетала во вчера).

function localDate(offsetDays = 0): string {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const EVENING_HOUR = 20;

export function useWeekReport(days = 7) {
  const { profile } = useUserStore();
  const [report, setReport] = useState<WeekReport | null>(null);
  const [rows, setRows] = useState<DayRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!profile?.id) return;
    let cancelled = false;

    (async () => {
      setLoading(true);

      // Окно: последние N дней, включая сегодня
      const from = localDate(-(days - 1));
      const to = localDate();
      const start = new Date(`${from}T00:00:00`);
      const end = new Date(`${to}T23:59:59.999`);

      const [{ data: checkins }, { data: meals }] = await Promise.all([
        supabase
          .from('daily_checkins')
          .select('date, mood, sleep, tags, note')
          .eq('user_id', profile.id)
          .gte('date', from)
          .lte('date', to),
        supabase
          .from('meals')
          .select('consumed_at, total_calories, total_carbs_g')
          .gte('consumed_at', start.toISOString())
          .lte('consumed_at', end.toISOString()),
      ]);

      if (cancelled) return;

      // Раскладываем еду по местным суткам
      const byDate = new Map<string, { calories: number; carbs: number; evening: number; count: number }>();
      for (const m of meals ?? []) {
        const dt = new Date((m as any).consumed_at);
        const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
        const bucket = byDate.get(key) ?? { calories: 0, carbs: 0, evening: 0, count: 0 };
        const kcal = Number((m as any).total_calories) || 0;
        bucket.calories += kcal;
        bucket.carbs += Number((m as any).total_carbs_g) || 0;
        if (dt.getHours() >= EVENING_HOUR) bucket.evening += kcal;
        bucket.count++;
        byDate.set(key, bucket);
      }

      const checkinByDate = new Map((checkins ?? []).map((c: any) => [c.date, c]));

      const built: DayRow[] = [];
      for (let i = days - 1; i >= 0; i--) {
        const date = localDate(-i);
        const c = checkinByDate.get(date);
        const f = byDate.get(date);
        built.push({
          date,
          mood: c?.mood ?? null,
          sleep: c?.sleep ?? null,
          tags: c?.tags ?? [],
          note: c?.note ?? null,
          calories: Math.round(f?.calories ?? 0),
          carbs: Math.round(f?.carbs ?? 0),
          eveningCalories: Math.round(f?.evening ?? 0),
          mealCount: f?.count ?? 0,
        });
      }

      const target = profile ? getActiveTargets(profile as any).calories ?? 0 : 0;
      setRows(built);
      setReport(buildWeekReport(built, Number(target) || 0));
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [profile?.id, days]);

  return { report, rows, loading };
}
